/**
 * Constellation filter chips (doc 147 §5.3).
 *
 * Four chip-styled `<select>`s — domain / tier / pack / isMinor. Native selects
 * keep it accessible and dependency-free; changing one re-tints the graph
 * without re-running layout.
 */
import { domainsSorted } from '../state/selectors.js';
import type { ConstellationFilters } from '../state/filters.js';
import type { AtlasManifest, SubscriptionTier, PackId } from '@atlas-manifest/types';

export interface ConstellationFiltersProps {
  manifest: AtlasManifest;
  filters: ConstellationFilters;
  onChange: (filters: ConstellationFilters) => void;
  /** How many entities currently pass the filters. */
  visibleCount: number;
  showGapsOnly?: boolean;
  onGapsToggle?: (value: boolean) => void;
}

const TIERS: SubscriptionTier[] = ['free', 'pro'];
const PACKS: PackId[] = ['business', 'investor', 'expat', 'family'];

export function ConstellationFilters({
  manifest,
  filters,
  onChange,
  visibleCount,
  showGapsOnly = false,
  onGapsToggle,
}: ConstellationFiltersProps): React.JSX.Element {
  const set = <K extends keyof ConstellationFilters>(
    key: K,
    value: ConstellationFilters[K],
  ): void => onChange({ ...filters, [key]: value });

  return (
    <div className="filter-bar" role="group" aria-label="Constellation filters">
      <select
        className="filter-chip"
        aria-label="Filter by domain"
        value={filters.domain}
        onChange={(e) => set('domain', e.target.value)}
      >
        <option value="all">all domains</option>
        {domainsSorted(manifest).map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </select>

      <select
        className="filter-chip"
        aria-label="Filter by tier"
        value={filters.tier}
        onChange={(e) => set('tier', e.target.value as ConstellationFilters['tier'])}
      >
        <option value="all">all tiers</option>
        {TIERS.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>

      <select
        className="filter-chip"
        aria-label="Filter by pack"
        value={filters.pack}
        onChange={(e) => set('pack', e.target.value as ConstellationFilters['pack'])}
      >
        <option value="all">all packs</option>
        {PACKS.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </select>

      <select
        className="filter-chip"
        aria-label="Filter by minor visibility"
        value={filters.isMinor}
        onChange={(e) => set('isMinor', e.target.value as ConstellationFilters['isMinor'])}
      >
        <option value="any">isMinor: any</option>
        <option value="visible">isMinor: visible</option>
        <option value="hidden">isMinor: hidden</option>
      </select>

      {onGapsToggle && (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            cursor: 'pointer',
            fontSize: 12,
            color: showGapsOnly ? '#f59e0b' : '#8b949e',
            fontWeight: showGapsOnly ? 600 : 400,
          }}
        >
          <input
            type="checkbox"
            aria-label="Show gaps only"
            checked={showGapsOnly}
            onChange={(e) => onGapsToggle(e.target.checked)}
            style={{ accentColor: '#f59e0b' }}
          />
          ⚠ Gaps
        </label>
      )}

      <span className="filter-count mono">
        {visibleCount} / {manifest.entities.length} nodes shown
      </span>
    </div>
  );
}
